/*
 * Rewards reveal grid — every tile is a photograph with its points copy on the
 * back. Tiles flip like cards to show the copy: on hover, and one at a time on
 * a timer.
 *
 * This is deliberately NOT the home page's rewards-grid.js. That grid swaps
 * content BETWEEN tiles, so it needs back-face loading, same-kind partner
 * pairing and a settle() step to reset the rotation for the next flip. Here
 * both faces are fixed — photo front, that tile's own copy back — so a reveal is
 * one attribute, and un-setting it rotates the tile back through the same
 * transition. The CSS owns the animation entirely.
 *
 * Only ONE tile is ever open at a time: the timer closes the previous tile
 * before opening the next, so the grid reads as a single card turning rather
 * than several going at once.
 */
if (!customElements.get("rewards-reveal-grid")) {
  customElements.define(
    "rewards-reveal-grid",
    class RewardsRevealGrid extends HTMLElement {
      connectedCallback() {
        this.tiles = Array.from(
          this.querySelectorAll(".wt-rewards-reveal__tile"),
        );
        if (!this.tiles.length) return;

        this.interval = parseInt(this.dataset.interval, 10) || 3200;
        this.hold = parseInt(this.dataset.hold, 10) || 2400;

        /* The one tile currently held open by the timer, and the timeout that
           will close it. Tracked as single values rather than a per-tile map:
           with one tile at a time there is only ever one of each. */
        this.current = null;
        this.closeTimer = null;

        /* Hover is the primary interaction and needs no JS — see the
           :hover/:focus-within rules in the stylesheet. The script only adds the
           automatic flip, so everything below is opt-in. */
        if (this.dataset.auto === "true") {
          /* Off-screen grids are not animated: a rewards grid partway down a
             long page would otherwise run for the entire visit. */
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) =>
                entry.isIntersecting ? this.start() : this.stop(),
              );
            },
            { rootMargin: "100px" },
          );
          this.observer.observe(this);

          /* While the pointer is in the grid the visitor is driving, so the
             timer stands down and the only tile turned is the one they chose. */
          this.addEventListener("pointerenter", this.onPointerEnter);
          this.addEventListener("pointerleave", this.onPointerLeave);

          document.addEventListener("shopify:section:unload", this.onUnload);
        }
      }

      disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
        this.removeEventListener("pointerenter", this.onPointerEnter);
        this.removeEventListener("pointerleave", this.onPointerLeave);
        document.removeEventListener("shopify:section:unload", this.onUnload);
        this.closeCurrent();
      }

      onUnload = () => this.stop();

      onPointerEnter = () => {
        this.hovered = true;
        this.sync();
        /* Close whatever the timer left open, so the visitor's hover is the only
           tile turned rather than competing with a stale one. */
        this.closeCurrent();
      };

      onPointerLeave = () => {
        this.hovered = false;
        this.sync();
      };

      start() {
        this.visible = true;
        this.sync();
      }

      stop() {
        this.visible = false;
        this.sync();
      }

      /* Two independent holds — off screen, and pointer inside. The timer runs
         only when neither applies, so one releasing does not override the other
         still being in force. */
      sync() {
        const shouldRun =
          this.visible &&
          !this.hovered &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (shouldRun && !this.timer) {
          this.timer = window.setInterval(() => this.tick(), this.interval);
          return;
        }

        if (!shouldRun && this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }

      /* Open one random tile, closing whichever was open first. Excluding the
         current tile from the candidates stops the same one being picked twice
         in a row, which would read as a tile that never closed. */
      tick() {
        const candidates = this.tiles.filter((tile) => tile !== this.current);
        if (!candidates.length) return;

        this.closeCurrent();

        const tile = candidates[Math.floor(Math.random() * candidates.length)];
        tile.dataset.revealed = "true";
        this.current = tile;

        this.closeTimer = window.setTimeout(() => this.closeCurrent(), this.hold);
      }

      /* Closing is just removing the attribute — the CSS transition runs in
         reverse, so the tile turns back rather than snapping. */
      closeCurrent() {
        if (this.closeTimer) {
          window.clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
        if (this.current) {
          delete this.current.dataset.revealed;
          this.current = null;
        }
      }
    },
  );
}
